import { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { getJobPostings } from '@/lib/contentful';
import type { JobPosting } from '@/types/job';
import JobCard from '@/components/JobCard';
import JobFilters from '@/components/JobFilters';

function HomePage() {
  const [jobs, setJobs] = useState<JobPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedType, setSelectedType] = useState('');

  useEffect(() => {
    async function fetchJobs() {
      setLoading(true);
      const data = await getJobPostings();
      setJobs(data);
      setLoading(false);
    }
    fetchJobs();
  }, []);

  const departments = useMemo(
    () => [...new Set(jobs.map((j) => j.department))].sort(),
    [jobs],
  );
  const locations = useMemo(
    () => [...new Set(jobs.map((j) => j.location))].sort(),
    [jobs],
  );
  const types = useMemo(
    () => [...new Set(jobs.map((j) => j.type))].sort(),
    [jobs],
  );

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      if (selectedDepartment && job.department !== selectedDepartment) return false;
      if (selectedLocation && job.location !== selectedLocation) return false;
      if (selectedType && job.type !== selectedType) return false;
      return true;
    });
  }, [jobs, selectedDepartment, selectedLocation, selectedType]);

  return (
    <>
      <Helmet>
        <title>Careers | ISHI Health</title>
        <meta
          name="description"
          content="Join the ISHI Health team. Explore open positions in engineering, clinical, operations, and more."
        />
      </Helmet>

      {/* Hero */}
      <section className="bg-brand-purple-dark">
        <div className="mx-auto max-w-content px-6 py-20 md:py-28">
          <h1 className="max-w-2xl font-heading text-4xl font-light leading-tight text-white md:text-5xl lg:text-6xl">
            Join Our Team
          </h1>
          <p className="mt-6 max-w-xl font-body text-lg leading-relaxed text-white/70">
            Help us shape the future of cardiac care with innovative technology,
            clinical excellence, and coordinated support. We're looking for
            passionate people to make a difference.
          </p>
        </div>
      </section>

      {/* Job Listings */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-content px-6 py-12 md:py-16">
          {/* Filters */}
          <div className="mb-8">
            <div className="mb-6">
              <h2 className="font-heading text-2xl font-semibold text-heading">
                Open Positions
              </h2>
              <p className="mt-1 font-body text-sm text-muted">
                {loading
                  ? 'Loading...'
                  : `${filteredJobs.length} position${filteredJobs.length !== 1 ? 's' : ''} available`}
              </p>
            </div>

            <JobFilters
              departments={departments}
              locations={locations}
              types={types}
              selectedDepartment={selectedDepartment}
              selectedLocation={selectedLocation}
              selectedType={selectedType}
              onDepartmentChange={setSelectedDepartment}
              onLocationChange={setSelectedLocation}
              onTypeChange={setSelectedType}
            />
          </div>

          {/* Results */}
          {loading ? (
            <div className="grid gap-4 md:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-xl border border-gray-100 bg-white p-6"
                >
                  <div className="mb-4 flex justify-between">
                    <div className="h-6 w-48 rounded bg-gray-200" />
                    <div className="h-6 w-20 rounded-full bg-gray-200" />
                  </div>
                  <div className="mb-4 flex gap-4">
                    <div className="h-4 w-24 rounded bg-gray-100" />
                    <div className="h-4 w-28 rounded bg-gray-100" />
                  </div>
                  <div className="h-3 w-32 rounded bg-gray-100" />
                </div>
              ))}
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="rounded-xl border border-gray-100 bg-white px-6 py-16 text-center">
              <svg
                className="mx-auto h-12 w-12 text-muted"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <h3 className="mt-4 font-heading text-lg font-semibold text-heading">
                No positions found
              </h3>
              <p className="mt-2 font-body text-sm text-muted">
                {jobs.length === 0
                  ? 'Check back soon for new openings.'
                  : 'Try adjusting your filters to see more results.'}
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filteredJobs.map((job) => (
                <JobCard key={job.slug} job={job} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Equal Opportunity Employer */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-content px-6 py-12 md:py-16">
          <h2 className="font-heading text-2xl font-semibold text-heading">
            Equal Opportunity Employer
          </h2>
          <p className="mt-4 max-w-3xl font-body text-body">
            ISHI Health is an equal opportunity employer. We are committed to
            creating an inclusive and respectful work environment for all
            employees.
          </p>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-brand-cream-dark">
        <div className="mx-auto max-w-content px-6 py-16 text-center md:py-20">
          <h2 className="font-heading text-2xl font-semibold text-heading md:text-3xl">
            Don't see the right role?
          </h2>
          <p className="mx-auto mt-4 max-w-lg font-body text-body">
            We're always looking for talented people. Send us your resume and
            we'll keep you in mind for future opportunities.
          </p>
          <a
            href="mailto:careers@ishi.health"
            className="btn-primary mt-8 inline-flex"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </>
  );
}

export default HomePage;
